import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SimpleGrid,
  Spinner,
  Center,
  Box,
  Skeleton,
  useColorModeValue,
} from '@chakra-ui/react';
import StatCard from './StatCard';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaClipboardList,
} from 'react-icons/fa';
import { FiUsers } from 'react-icons/fi';
import {
  fetchSurveyStats,
  selectSurveyStats,
} from '../../../redux/slices/surveyStatsSlice';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const iconMap = {
  'Total Survey': FaClipboardList,
  Approved: FaCheckCircle,
  Rejected: FaTimesCircle,
  Pending: FaHourglassHalf,
  'Active Talatics': FiUsers,
};

const colorMap = {
  'Total Survey': {
    bgColor: '#ffe4b5',
    waveColor: '#f6ad55',
    color: 'orange.500',
  },
  Approved: {
    bgColor: '#c6f6d5',
    waveColor: '#48bb78',
    color: 'green.500',
  },
  Rejected: {
    bgColor: '#fed7d7',
    waveColor: '#f56565',
    color: 'red.500',
  },
  Pending: {
    bgColor: '#bee3f8',
    waveColor: '#4299e1',
    color: 'blue.500',
  },
};

const SurveyStatsCards = () => {
  const dispatch = useDispatch();
  const MotionBox = motion(Box);
  const { data, loading } = useSelector((state) => state.surveyStats);
  const navigate = useNavigate();
  const skeletonBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    if (!data) {
      dispatch(fetchSurveyStats());
    }
  }, [dispatch, data]);

  const formattedStats = useMemo(() => {
    if (!data) return [];

    const { totalCount, approved, rejected, pending } = data;

    return [
      { label: 'Total Survey', count: totalCount },
      { label: 'Approved', count: approved },
      { label: 'Rejected', count: rejected },
      { label: 'Pending', count: pending },
    ].map((stat) => ({
      ...stat,
      icon: iconMap[stat.label],
      ...colorMap[stat.label],
    }));
  }, [data]);

  if (loading || !data) {
    return (
      <SimpleGrid
        columns={{ base: 1, sm: 2, md: 2, lg: 4 }}
        spacing="6"
        mb="10"
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Box
            key={index}
            p={{ base: 3, md: 4 }}
            borderWidth="1px"
            borderRadius="lg"
            boxShadow="md"
            bg={skeletonBg}
            borderColor={borderColor}
            minH={{ base: '140px', md: '170px' }}
          >
            <Skeleton height="24px" width="60px" mb="4" />
            <Skeleton height="28px" width="80%" mb="2" />
            <Skeleton height="20px" width="40%" />
          </Box>
        ))}
      </SimpleGrid>
    );
  }

  const handleClick = (params) => {
    switch (params?.name) {
      case 'Pending':
        navigate('/admin/pendinglist');
        break;
      case 'Approved':
        navigate('/admin/surveylist?status=approved');
        break;
      case 'Rejected':
        navigate('/admin/surveylist?status=rejected');
        break;
      case 'Total Survey':
        navigate('/admin/surveylist');
        break;
      default:
        break;
    }
  };

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 2, lg: 4 }} spacing="6" mb="10">
      {formattedStats.map((stat, index) => (
        <MotionBox
          key={stat.label}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.15 }}
        >
          <StatCard
            key={index}
            {...stat}
            onClick={() => handleClick({ name: stat.label })}
          />
        </MotionBox>
      ))}
    </SimpleGrid>
  );
};

export default SurveyStatsCards;
